import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { CreateWebsiteBannerDto } from '@/generated/korza-api-client/types.gen';
import { useI18nContext } from '@/i18n/i18n-react';
import {
  getPositionMode,
  getPositionSelectorValue,
  type PositionMode,
} from '@/lib/website-banner-constants';

type Position = NonNullable<CreateWebsiteBannerDto['position']>;

export interface PositionPanelProps {
  form: CreateWebsiteBannerDto;
  updatePosition: (patch: Partial<Position>) => void;
}

const NO_POSITION: Position = {
  placeInside: null,
  placeAfter: null,
  placeBefore: null,
};

/** Where the banner goes in the page: default or relative to a CSS selector. */
export function PositionPanel({ form, updatePosition }: PositionPanelProps) {
  const { LL } = useI18nContext();
  const current = getPositionMode(form.position);
  const selector = getPositionSelectorValue(form.position);

  const modes: {
    mode: PositionMode;
    label: string;
    description: string;
    patch: Position;
  }[] = [
    {
      mode: 'default',
      label: LL.websiteBannerPage.advanced.positionDefault(),
      description: LL.websiteBannerPage.advanced.positionDescriptionDefault(),
      patch: NO_POSITION,
    },
    {
      mode: 'child',
      label: LL.websiteBannerPage.advanced.positionChildOf(),
      description: LL.websiteBannerPage.advanced.positionDescriptionChild(),
      patch: { ...NO_POSITION, placeInside: selector },
    },
    {
      mode: 'before',
      label: LL.websiteBannerPage.advanced.positionBefore(),
      description: LL.websiteBannerPage.advanced.positionDescriptionBefore(),
      patch: { ...NO_POSITION, placeBefore: selector },
    },
    {
      mode: 'after',
      label: LL.websiteBannerPage.advanced.positionAfter(),
      description: LL.websiteBannerPage.advanced.positionDescriptionAfter(),
      patch: { ...NO_POSITION, placeAfter: selector },
    },
  ];

  const onSelectorChange = (value: string) => {
    const next = value || null;
    if (current === 'child') updatePosition({ placeInside: next });
    else if (current === 'before') updatePosition({ placeBefore: next });
    else if (current === 'after') updatePosition({ placeAfter: next });
  };

  return (
    <div className="space-y-3">
      <Label>{LL.websiteBannerPage.advanced.positionLabel()}</Label>
      <div role="group" className="flex flex-wrap gap-2">
        {modes.map(({ mode, label, patch }) => (
          <Button
            key={mode}
            type="button"
            size="sm"
            variant={current === mode ? 'default' : 'outline'}
            className="max-md:min-h-touch"
            aria-pressed={current === mode}
            onClick={() => updatePosition(patch)}
          >
            {label}
          </Button>
        ))}
      </div>
      {current !== 'default' && (
        <div className="space-y-1.5">
          <Label htmlFor="position-selector">
            {LL.websiteBannerPage.advanced.selectorLabel()}
          </Label>
          <Input
            id="position-selector"
            className="w-full md:w-auto md:min-w-[30ch]"
            value={selector}
            onChange={(e) => onSelectorChange(e.target.value)}
            placeholder={LL.websiteBannerPage.advanced.selectorPlaceholder()}
          />
        </div>
      )}
      <p className="text-sm text-muted-foreground">
        {modes.find(({ mode }) => mode === current)?.description}
      </p>
    </div>
  );
}
